/**
 * TeleTime POS - Service Worker Update Plumbing (Piece A)
 *
 * Turns "a new SW is waiting" into a controlled, verifiable update path:
 *
 *  - controllerchange → ONE guarded page reload. This is what converts "new
 *    SW activated" into "page is running the new bundle". Never a hot-swap:
 *    ~20 screens (incl. CheckoutModal) are lazy-loaded, and a new SW under a
 *    running old page would 404 their old hashed chunks.
 *  - applyUpdate() → posts { type: 'SKIP_WAITING' } to the waiting worker.
 *    The SW only skipWaiting()s on this message, so the PAGE decides when.
 *  - onUpdateWaiting(cb) → fires when an update is ready to apply (either
 *    already waiting at registration time, or freshly installed while the
 *    page is controlled). Pieces B (update banner) and C (safe-point
 *    auto-apply) consume this; Piece A wires no UI and no auto-trigger.
 */

// Piece B: how often an always-open register asks the browser to re-check
// sw.js — this is what makes a deploy noticeable without any navigation.
// Focus and 'online' also trigger a check, so 20 min is a ceiling, not a lag.
export const UPDATE_POLL_INTERVAL_MS = 20 * 60_000;

export function createSwUpdater({ container, reload, windowRef = typeof window !== 'undefined' ? window : null }) {
  let reloading = false;
  let registration = null;
  let pollTimer = null;
  const listeners = new Set();

  const notify = (worker) => listeners.forEach((cb) => cb(worker));

  // update() rejects while offline / server unreachable — routine on a store
  // floor, never an error worth surfacing.
  const checkForUpdate = () => {
    registration?.update().catch(() => {});
  };

  // Single guarded reload — the flag makes a reload loop impossible even if
  // controllerchange fires more than once before navigation completes.
  container.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    reload();
  });

  return {
    setRegistration(reg) {
      registration = reg;

      // Update already waiting (deployed while the terminal sat on an old
      // page / before this load) — surface it immediately.
      if (reg.waiting) notify(reg.waiting);

      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          // 'installed' with an existing controller = an UPDATE is waiting.
          // (First-ever install has no controller — nothing to apply.)
          if (newWorker.state === 'installed' && container.controller) {
            notify(newWorker);
          }
        });
      });
    },

    getWaitingWorker() {
      return registration?.waiting || null;
    },

    /** Ask the waiting SW to activate. Returns false if nothing is waiting. */
    applyUpdate() {
      const waiting = registration?.waiting;
      if (!waiting) return false;
      waiting.postMessage({ type: 'SKIP_WAITING' });
      return true;
    },

    onUpdateWaiting(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },

    /**
     * Piece B: poll registration.update() on an interval + on window focus +
     * on 'online'. Idempotent — returns false (and stacks nothing) if polling
     * is already running, so remounts can't duplicate timers.
     */
    startPolling(intervalMs = UPDATE_POLL_INTERVAL_MS) {
      if (pollTimer !== null) return false;
      pollTimer = setInterval(checkForUpdate, intervalMs);
      windowRef?.addEventListener('focus', checkForUpdate);
      windowRef?.addEventListener('online', checkForUpdate);
      return true;
    },

    stopPolling() {
      if (pollTimer === null) return;
      clearInterval(pollTimer);
      pollTimer = null;
      windowRef?.removeEventListener('focus', checkForUpdate);
      windowRef?.removeEventListener('online', checkForUpdate);
    },
  };
}

// ----------------------------------------------------------------------------
// App-wide singleton (main.jsx wires the registration; B/C consume the API)
// ----------------------------------------------------------------------------

// Insecure-context guard: service workers don't exist on non-secure origins
// (e.g. http://<raw-IP>:5000 — navigator.serviceWorker is undefined there).
// Dereferencing it crashed the whole POS to the ErrorBoundary the moment
// <UpdateBanner /> mounted. On such origins the updater is an inert no-op:
// no machinery, no banner, no crash — a plain reload already fetches fresh
// code there, since nothing intercepts.
const NOOP_UPDATER = Object.freeze({
  setRegistration() {},
  getWaitingWorker() { return null; },
  applyUpdate() { return false; },
  onUpdateWaiting() { return () => {}; },
  startPolling() { return false; },
  stopPolling() {},
});

let defaultUpdater = null;

export function getSwUpdater() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return NOOP_UPDATER;
  }
  if (!defaultUpdater) {
    defaultUpdater = createSwUpdater({
      container: navigator.serviceWorker,
      reload: () => window.location.reload(),
    });
  }
  return defaultUpdater;
}
